export const PAGE_HELP_OPEN_EVENT = 'sit:page-help';
export const PAGE_HELP_REGISTER_EVENT = 'sit:page-help-register';
export const PAGE_HELP_UNREGISTER_EVENT = 'sit:page-help-unregister';

export interface PageHelpFilter {
  id: string;
  label: string;
  value?: string;
}

export interface PageHelpScreenState {
  loading?: boolean;
  error?: string;
  total?: number;
  visibleCount?: number;
  selectedCount?: number;
  currentPage?: number;
  totalPages?: number;
  activeView?: string;
  filters?: PageHelpFilter[];
}

export interface PageHelpAnchor {
  id: string;
  label: string;
  description: string;
  category?: string;
  selector?: string;
  getElement?: () => HTMLElement | null;
}

export interface PageHelpAction {
  id: string;
  label: string;
  description?: string;
  kind?: 'primary' | 'secondary';
  anchorId?: string;
  disabled?: boolean;
  run: () => void;
}

export interface PageHelpSection {
  id: string;
  title: string;
  summary?: string;
  items?: string[];
  anchorIds?: string[];
}

export interface PageHelpDefinition {
  screenId: string;
  title: string;
  subtitle?: string;
  entity: {
    singular: string;
    plural: string;
  };
  state?: PageHelpScreenState;
  anchors?: PageHelpAnchor[];
  actions?: PageHelpAction[];
  sections?: PageHelpSection[];
  observations?: PageHelpObservation[];
  useDefaultSelectionObservation?: boolean;
}

export interface PageHelpObservation {
  id: string;
  tone: 'neutral' | 'positive' | 'warning' | 'danger';
  title: string;
  text: string;
}

export interface PageHelpRegistration {
  update: (definition: PageHelpDefinition) => void;
  unregister: () => void;
}

type PageHelpListener = (definition: PageHelpDefinition | null) => void;

interface RegisteredPageHelp {
  definition: PageHelpDefinition;
  order: number;
}

const definitions = new Map<string, RegisteredPageHelp>();
const listeners = new Set<PageHelpListener>();
let registrationSequence = 0;

function getActiveDefinition(): PageHelpDefinition | null {
  let active: RegisteredPageHelp | null = null;
  definitions.forEach(entry => {
    if (!active || entry.order > active.order) active = entry;
  });
  return active ? active.definition : null;
}

function notifyPageHelpListeners(): void {
  const activeDefinition = getActiveDefinition();
  listeners.forEach(listener => listener(activeDefinition));
}

/**
 * Registers a state-aware help definition for the mounted screen. The latest
 * mounted definition wins, so drawers and nested full-screen flows can safely
 * supply their own help and reveal the underlying screen again on unmount.
 */
export function registerPageHelpDefinition(definition: PageHelpDefinition): PageHelpRegistration {
  const registrationId = `${definition.screenId}:${++registrationSequence}`;
  definitions.set(registrationId, { definition, order: registrationSequence });
  notifyPageHelpListeners();

  let isRegistered = true;
  return {
    update: nextDefinition => {
      const current = definitions.get(registrationId);
      if (!isRegistered || !current) return;
      definitions.set(registrationId, { ...current, definition: nextDefinition });
      notifyPageHelpListeners();
    },
    unregister: () => {
      if (!isRegistered) return;
      isRegistered = false;
      definitions.delete(registrationId);
      notifyPageHelpListeners();
    }
  };
}

export function subscribePageHelpDefinitions(listener: PageHelpListener): () => void {
  listeners.add(listener);
  listener(getActiveDefinition());
  return () => listeners.delete(listener);
}

export function getPageHelpAnchorElement(anchor: PageHelpAnchor): HTMLElement | null {
  if (anchor.getElement) return anchor.getElement();
  const selector = anchor.selector || `[data-help-anchor="${anchor.id}"]`;
  return document.querySelector(selector) as HTMLElement | null;
}

function formatCount(value: number): string {
  return value.toLocaleString('en-GB');
}

export function buildPageHelpObservations(definition: PageHelpDefinition): PageHelpObservation[] {
  const state = definition.state || {};
  const filters = state.filters || [];
  const plural = definition.entity.plural;
  const observations: PageHelpObservation[] = [];

  if (state.loading) {
    observations.push({
      id: 'loading',
      tone: 'neutral',
      title: `Loading ${plural}`,
      text: 'The latest information is being retrieved now.'
    });
    return observations;
  }

  if (state.error) {
    observations.push({
      id: 'error',
      tone: 'danger',
      title: `We could not load ${plural}`,
      text: state.error
    });
    return observations;
  }

  if (definition.observations) observations.push(...definition.observations);

  if (state.total === 0 && filters.length === 0) {
    observations.push({
      id: 'empty',
      tone: 'neutral',
      title: `No ${plural} here yet`,
      text: `No ${plural} have been created for this view. Use the main action on this page when you are ready to add one.`
    });
  } else if (state.total === 0 && filters.length > 0) {
    observations.push({
      id: 'empty-filtered',
      tone: 'warning',
      title: `No ${plural} match these filters`,
      text: `You have ${filters.length} active ${
        filters.length === 1 ? 'filter' : 'filters'
      }. Remove one or clear them all to widen the results.`
    });
  } else if (typeof state.total === 'number') {
    const pageCount = state.totalPages || 1;
    const currentPage = state.currentPage || 1;
    observations.push({
      id: 'results',
      tone: 'positive',
      title: `${formatCount(state.total)} ${plural} found`,
      text:
        pageCount === 1
          ? `All ${formatCount(
              state.total
            )} matching ${plural} fit on this single page. Use search or filters to find a specific record.`
          : `There are ${formatCount(pageCount)} pages and you are on page ${formatCount(
              currentPage
            )}. Use pagination to move between them, or search and filters to find a specific record.`
    });
  }

  if (state.selectedCount && definition.useDefaultSelectionObservation !== false) {
    observations.push({
      id: 'selection',
      tone: 'neutral',
      title: `${formatCount(state.selectedCount)} selected`,
      text: 'Actions in the selection bar apply to the selected records.'
    });
  }

  return observations;
}

/** Compatibility bridge for existing page-owned static help modals. */
export function registerPageHelp(onOpen: () => void, label = 'How this page works'): () => void {
  const openLegacyHelp = (event: Event) => {
    const source = (event as CustomEvent<{ source?: string }>).detail?.source;
    if (source && source !== 'legacy') return;
    onOpen();
  };
  window.addEventListener(PAGE_HELP_OPEN_EVENT, openLegacyHelp);
  window.dispatchEvent(new CustomEvent(PAGE_HELP_REGISTER_EVENT, { detail: { label, source: 'legacy' } }));

  return () => {
    window.removeEventListener(PAGE_HELP_OPEN_EVENT, openLegacyHelp);
    window.dispatchEvent(new CustomEvent(PAGE_HELP_UNREGISTER_EVENT, { detail: { source: 'legacy' } }));
  };
}
